import { Box, Chip, Typography } from '@mui/material';

import type { NetworkCampaign } from '@common/types/campaign';
import { CampaignCardImage } from '@common/components/CampaignCardImage';
import { JAPANESE_DATE_TIME_FORMAT_NO_PADDING } from '@common/constants';
import { dayJST } from '@common/utils/date';
import { ChevronRightRedCircle } from './ChevronRedCircle';

type CampaignCardItemProps = {
  campaign: NetworkCampaign;
  href: string;
  openCampaignInNewTab: boolean;
};

/**
 * Campaign card item component
 *
 * @param props.campaign - The campaign object
 * @param props.href - The href of the campaign
 * @param props.openCampaignInNewTab - Whether to open the campaign in a new tab
 * @returns The rendered campaign card item
 */
export function CampaignCardItem({
  campaign,
  href,
  openCampaignInNewTab,
}: CampaignCardItemProps) {
  return (
    <Box
      component="a"
      data-test-id="campaign-item-selector"
      href={href}
      rel={openCampaignInNewTab ? 'noopener noreferrer' : undefined}
      target={openCampaignInNewTab ? '_blank' : undefined}
      sx={(theme) => ({
        '&, & *': {
          cursor: 'pointer',
        },
        '&:hover': { opacity: 0.8 },
        color: 'inherit',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        textDecoration: 'none',
        [theme.breakpoints.down('md')]: {
          marginTop: '32px',
        },
        width: '100%',
      })}
    >
      <CampaignCardImage
        alt={campaign.campaignName}
        revealKey={campaign.campaignId}
        srcPc={campaign.campaignImagePc}
        srcSp={campaign.campaignImageSp}
      />

      {campaign.publicFlag === 0 && (
        <Box sx={{ marginTop: '8px' }}>
          <Chip
            label={
              <Typography
                sx={(theme) => ({
                  color: theme.palette?.text?.primary ?? theme.colors.grey[900],
                  fontSize: '13px',
                  fontWeight: 300,
                  lineHeight: '20.3px',
                })}
              >
                限定公開
              </Typography>
            }
            sx={(theme) => ({
              backgroundColor: theme.colors.orangeLight1,
              borderRadius: '0px',
              padding: '4px 7px',
            })}
          />
        </Box>
      )}

      <Box
        sx={{
          alignItems: 'flex-start',
          display: 'flex',
          marginTop: '20px',
          minWidth: 0,
        }}
      >
        <ChevronRightRedCircle
          scale={1.92}
          styles={{
            flexShrink: 0,
            marginLeft: '8px',
            marginTop: '8px',
          }}
        />
        <Typography
          data-test-id="campaign-name-selector"
          sx={(theme) => ({
            color: theme.palette?.text?.primary ?? theme.colors.grey[900],
            flex: 1,
            fontSize: '18px',
            fontWeight: 700,
            lineHeight: '27px',
            marginLeft: '8px',
            minWidth: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          })}
        >
          {campaign.campaignName}
        </Typography>
      </Box>

      <Box sx={{ marginTop: '14px' }}>
        <Typography
          component="span"
          sx={(theme) => ({
            color: theme.palette?.text?.primary ?? theme.colors.grey[900],
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '21px',
          })}
        >
          応募締切 :&nbsp;
        </Typography>
        <Typography
          component="span"
          data-test-id="campaign-date-selector"
          sx={(theme) => ({
            color: theme.colors.primary,
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '21px',
          })}
        >
          {dayJST(campaign.applicationEndDatetime).format(
            JAPANESE_DATE_TIME_FORMAT_NO_PADDING
          )}
          まで
        </Typography>
      </Box>
    </Box>
  );
}
